import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../api/config';
import toast from 'react-hot-toast';
import {
  Bell, BellOff, Clock, CheckCircle, XCircle,
  Phone, MapPin, Banknote, RefreshCw, User, Zap,
  AlertTriangle, Timer, Package, Bike, UtensilsCrossed,
  ChefHat, PackageCheck, Truck, Home, ShoppingBag,
} from 'lucide-react';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// AUDIO ENGINE — MP3 based (order_alert.mp3)
// ✅ MP3 file: public/order_alert.mp3 mein rakh
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
let _alertAudio   = null; // current Audio instance
let _bellRepeatId = null; // repeat interval id

// Create a fresh Audio instance (needed for mobile replay)
function createAlertAudio() {
  const audio = new Audio('/order_alert.mp3');
  audio.loop   = false;
  audio.volume = 1.0;
  return audio;
}

// Unlock audio on first user gesture (iOS/Android requirement)
export function unlockAudio() {
  try {
    const a = createAlertAudio();
    // Play and immediately pause — this unlocks the audio context
    a.play().then(() => a.pause()).catch(() => {});
  } catch {}
}

// ✅ stopOrderAlert — buttons call karte hain isko
export function stopOrderAlert() {
  if (_bellRepeatId) {
    clearInterval(_bellRepeatId);
    _bellRepeatId = null;
  }
  if (_alertAudio) {
    _alertAudio.pause();
    _alertAudio.currentTime = 0;
    _alertAudio = null;
  }
}

// Play MP3 once
function playAlertOnce() {
  try {
    if (_alertAudio) {
      _alertAudio.pause();
      _alertAudio.currentTime = 0;
    }
    _alertAudio = createAlertAudio();
    _alertAudio.play().catch(e => console.warn('Alert sound failed:', e.message));
  } catch (e) {
    console.warn('Alert sound error:', e.message);
  }
}

// Keep ringing until stopOrderAlert() is called
function startOrderAlert() {
  stopOrderAlert();
  playAlertOnce();
  // Replay every 5 seconds (adjust to match your MP3 length)
  _bellRepeatId = setInterval(playAlertOnce, 5000);
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAT CONFIG
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const TAT = {
  placed:    { label: 'Accept order',      total: 50,     color: '#ef4444', urgentAt: 20 },
  pending:   { label: 'Accept order',      total: 50,     color: '#ef4444', urgentAt: 20 },
  accepted:  { label: 'Start preparing',   total: 4 * 60, color: '#f97316', urgentAt: 60 },
  preparing: { label: 'Mark as ready',     total: 4 * 60, color: '#f97316', urgentAt: 60 },
  packed:    { label: 'Mark as ready',     total: 4 * 60, color: '#f97316', urgentAt: 60 },
  ready:     { label: 'Waiting for rider', total: 1 * 60, color: '#8b5cf6', urgentAt: 20 },
};

const SC = {
  placed:       { label: 'New Order',    color: '#ef4444', bg: '#fef2f2' },
  pending:      { label: 'New Order',    color: '#ef4444', bg: '#fef2f2' },
  accepted:     { label: 'Accepted',     color: '#f97316', bg: '#fff7ed' },
  preparing:    { label: 'In Kitchen',   color: '#8b5cf6', bg: '#f5f3ff' },
  packing:      { label: 'Packing',      color: '#8b5cf6', bg: '#f5f3ff' },
  packed:       { label: 'Packed',       color: '#06b6d4', bg: '#ecfeff' },
  ready:        { label: 'Ready',        color: '#06b6d4', bg: '#ecfeff' },
  picked_up:    { label: 'Picked Up',    color: '#7c3aed', bg: '#f5f3ff' },
  'on-the-way': { label: 'On the Way',   color: '#10b981', bg: '#f0fdf4' },
  delivered:    { label: 'Delivered',    color: '#10b981', bg: '#f0fdf4' },
  cancelled:    { label: 'Cancelled',    color: '#6b7280', bg: '#f9fafb' },
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAT TIMER HOOK
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function useTATTimer(order) {
  const [elapsed, setElapsed] = useState(0);
  const iv = useRef(null);

  useEffect(() => {
    const tat = TAT[order.status];
    if (!tat) { setElapsed(0); return; }

    const statusStarted = (() => {
      if (['placed', 'pending'].includes(order.status)) return new Date(order.createdAt);
      if (order.status === 'accepted')                  return order.acceptedAt  ? new Date(order.acceptedAt)  : new Date(order.createdAt);
      if (['preparing', 'packed'].includes(order.status)) return (order.preparingAt || order.packedAt) ? new Date(order.preparingAt || order.packedAt) : new Date(order.createdAt);
      if (order.status === 'ready')                     return order.readyAt     ? new Date(order.readyAt)     : new Date(order.createdAt);
      return new Date(order.createdAt);
    })();

    const update = () => setElapsed(Math.floor((Date.now() - statusStarted.getTime()) / 1000));
    update();
    iv.current = setInterval(update, 1000);
    return () => clearInterval(iv.current);
  }, [order._id, order.status]);

  const tat = TAT[order.status];
  if (!tat) return null;

  const remaining = Math.max(0, tat.total - elapsed);
  const pct       = Math.min(100, (elapsed / tat.total) * 100);
  const isUrgent  = remaining <= tat.urgentAt && remaining > 0;
  const isExpired = remaining === 0;
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  return {
    remaining, pct, isUrgent, isExpired,
    timeStr: mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : `${secs}s`,
    label: tat.label, color: tat.color,
  };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAT TIMER BAR
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function TATBar({ timer }) {
  if (!timer) return null;
  const barColor = timer.isExpired ? '#ef4444' : timer.isUrgent ? '#f97316' : '#10b981';
  const bgColor  = timer.isExpired ? '#fef2f2' : timer.isUrgent ? '#fff7ed' : '#f0fdf4';
  const bdColor  = timer.isExpired ? '#fecaca' : timer.isUrgent ? '#fed7aa' : '#bbf7d0';

  return (
    <div style={{
      background: bgColor, borderRadius: 10, padding: '8px 12px', marginBottom: 10,
      border: `1.5px solid ${bdColor}`,
      animation: timer.isExpired ? 'urgentPulse 0.5s infinite' : timer.isUrgent ? 'urgentPulse 1s infinite' : 'none',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Timer size={13} color={barColor} />
          <span style={{ fontSize: 12, fontWeight: 700, color: barColor }}>
            {timer.isExpired ? '⚠️ Time is up!' : timer.label}
          </span>
        </div>
        <span style={{ fontSize: 15, fontWeight: 900, color: barColor, fontVariantNumeric: 'tabular-nums' }}>
          {timer.isExpired ? '00:00' : timer.timeStr}
        </span>
      </div>
      <div style={{ height: 5, background: 'rgba(0,0,0,0.08)', borderRadius: 100, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${Math.max(0, 100 - timer.pct)}%`,
          background: barColor, borderRadius: 100, transition: 'width 1s linear',
        }} />
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ORDER CARD
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function OrderCard({ order, updateStatus, cancelOrder, isGrocery = false }) {
  const s        = SC[order.status] || SC.placed;
  const customer = order.user || order.customer;
  const amount   = order.total || order.totalAmount || 0;
  const timer    = useTATTimer(order);
  const isNew    = ['placed', 'pending'].includes(order.status);

  const NEXT_MAP = {
    placed:    'accepted',
    pending:   'accepted',
    accepted:  isGrocery ? 'packing' : 'preparing',
    preparing: 'ready',
    packing:   'packed',
    packed:    'ready',
  };
  const next = NEXT_MAP[order.status];

  const NEXT_BTN = {
    accepted:  { label: isGrocery ? '📦 Start Packing' : '👨‍🍳 Start Cooking', color: '#8b5cf6' },
    preparing: { label: '✅ Ready for Pickup',                                  color: '#06b6d4' },
    packing:   { label: '📦 Packing Done',                                      color: '#8b5cf6' },
    packed:    { label: '✅ Packed & Ready',                                    color: '#06b6d4' },
  };
  const nextBtn = next ? NEXT_BTN[order.status] : null;

  useEffect(() => {
    if (timer?.isExpired && isNew) {
      toast.error(`⚠️ Order #${order._id.slice(-6).toUpperCase()} — Accept time expired!`, {
        id: order._id, duration: 5000,
      });
    }
  }, [timer?.isExpired]);

  return (
    <div style={{
      ...C.card,
      border: timer?.isExpired ? '2px solid #ef4444'
        : timer?.isUrgent ? '2px solid #f97316'
        : isNew ? '2px solid #fbbf24'
        : '1px solid #f0f0f0',
      boxShadow: timer?.isExpired ? '0 0 0 3px rgba(239,68,68,0.12)'
        : timer?.isUrgent ? '0 0 0 3px rgba(249,115,22,0.12)'
        : '0 2px 12px rgba(0,0,0,0.07)',
    }}>

      {/* Header */}
      <div style={C.head}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={C.id}>#{order._id.slice(-6).toUpperCase()}</span>
          <span style={{ ...C.statusBadge, background: s.bg, color: s.color }}>{s.label}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {(timer?.isUrgent || timer?.isExpired) && (
            <AlertTriangle size={14} color={timer.isExpired ? '#ef4444' : '#f97316'} />
          )}
          <span style={C.age}>
            <Clock size={11} color='#bbb' />
            {(() => {
              const m = Math.floor((Date.now() - new Date(order.createdAt)) / 60000);
              if (m < 1) return 'Just now';
              if (m < 60) return `${m}m ago`;
              return `${Math.floor(m / 60)}h ago`;
            })()}
          </span>
        </div>
      </div>

      <TATBar timer={timer} />

      {/* Customer */}
      <div style={C.row}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <User size={15} color='#888' />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>{customer?.name || 'Customer'}</div>
            {customer?.phone && <div style={{ fontSize: 11, color: '#aaa' }}>{customer.phone}</div>}
          </div>
        </div>
        {customer?.phone && (
          <a href={`tel:${customer.phone}`} style={C.callBtn}>
            <Phone size={13} color='#16a34a' /> Call
          </a>
        )}
      </div>

      {/* Items */}
      <div style={C.items}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Package size={11} color='#aaa' /> {isGrocery ? 'Products' : 'Items'}
        </div>
        {(order.items || []).map((item, i) => (
          <div key={i} style={C.item}>
            <span style={{ fontSize: 13, color: '#333' }}>{item.name}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#888', fontWeight: 600 }}>x{item.qty || item.quantity || 1}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>₹{(item.price || 0) * (item.qty || item.quantity || 1)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Delivery address */}
      {order.deliveryAddress && (
        <div style={C.addr}>
          <MapPin size={12} color='#bbb' />
          <span style={{ fontSize: 12, color: '#888', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[order.deliveryAddress.line1, order.deliveryAddress.area, order.deliveryAddress.city].filter(Boolean).join(', ')
              || order.deliveryAddress.street || order.deliveryAddress.address || 'Address on file'}
          </span>
        </div>
      )}

      {/* Total */}
      <div style={C.foot}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Banknote size={15} color='#ff6b35' />
          <span style={{ fontWeight: 900, fontSize: 17, color: '#ff6b35' }}>₹{amount}</span>
          {order.paymentMode && (
            <span style={C.payMode}>{order.paymentMode.toUpperCase()}</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div style={C.actions}>
        {isNew && (
          <>
            <button
              style={{ ...C.acceptBtn, animation: timer?.isUrgent || timer?.isExpired ? 'btnPulse 0.8s infinite' : 'none' }}
              onClick={() => { unlockAudio(); stopOrderAlert(); updateStatus(order._id, 'accepted'); }}
            >
              <CheckCircle size={15} color='#fff' /> Accept Order
            </button>
            <button style={C.rejectBtn} onClick={() => { unlockAudio(); stopOrderAlert(); cancelOrder(order._id); }}>
              <XCircle size={15} color='#ef4444' /> Reject
            </button>
          </>
        )}

        {next && !isNew && nextBtn && (
          <button
            style={{
              ...C.nextBtn,
              background: nextBtn.color,
              animation: timer?.isUrgent || timer?.isExpired ? 'btnPulse 0.8s infinite' : 'none',
            }}
            onClick={() => { unlockAudio(); stopOrderAlert(); updateStatus(order._id, next); }}
          >
            {order.status === 'accepted'
              ? (isGrocery ? <Package size={15} color='#fff' /> : <ChefHat size={15} color='#fff' />)
              : <PackageCheck size={15} color='#fff' />}
            {nextBtn.label}
          </button>
        )}
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN DASHBOARD
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export default function Dashboard() {
  const { vendor }  = useAuth();
  const [soundOn, setSoundOn]       = useState(true);
  const [activeOrders, setActiveOrders] = useState([]);
  const [loading, setLoading]       = useState(true);
  const audioReady  = useRef(false);
  const prevNewIds  = useRef(new Set());

  const vd        = vendor?.vendor || vendor;
  const vendorEmail = vendor?.email || vd?.email;
  const isGrocery = (vd?.businessType || '').toLowerCase() === 'grocery';

  // ── Fetch orders ─────────────────────────────
  const fetchOrders = useCallback(async (silent = false) => {
    if (!vendorEmail) return;
    if (!silent) setLoading(true);
    try {
      const res = await API.get(`/orders/vendor/active?vendorEmail=${encodeURIComponent(vendorEmail)}`);
      const orders = res.data?.orders || res.data || [];
      setActiveOrders(orders);

      // Ring bell if new placed orders arrived
      if (soundOn) {
        const newIds = new Set(
          orders.filter(o => ['placed', 'pending'].includes(o.status)).map(o => o._id)
        );
        const hasNew = [...newIds].some(id => !prevNewIds.current.has(id));
        if (hasNew) startOrderAlert();
        else if (newIds.size === 0) stopOrderAlert();
        prevNewIds.current = newIds;
      }
    } catch {
      if (!silent) toast.error('Orders load nahi hue!');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [vendorEmail, soundOn]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    fetchOrders();
    const iv = setInterval(() => fetchOrders(true), 15000);
    return () => { clearInterval(iv); stopOrderAlert(); };
  }, [fetchOrders]);

  // Stop bell when sound toggled off
  useEffect(() => {
    if (!soundOn) stopOrderAlert();
  }, [soundOn]);

  // Unlock audio on first user interaction
  useEffect(() => {
    const unlock = () => {
      if (!audioReady.current) { unlockAudio(); audioReady.current = true; }
    };
    window.addEventListener('touchstart', unlock, { once: true });
    window.addEventListener('click',      unlock, { once: true });
    return () => {
      window.removeEventListener('touchstart', unlock);
      window.removeEventListener('click',      unlock);
    };
  }, []);

  // ── Status update ─────────────────────────────
  const updateStatus = async (orderId, status) => {
    stopOrderAlert();
    try {
      const res  = await API.patch(`/orders/${orderId}/status`, { status });
      const data = res.data;

      if (status === 'accepted') {
        toast.success('Order accepted! ' + (isGrocery ? 'Start packing.' : 'Start cooking.'), { duration: 3000 });
      } else if (status === 'preparing') {
        toast.success('Cooking started! Mark as ready when done.');
      } else if (status === 'packed') {
        toast.success('Packing started! Mark as ready when done.');
      } else if (status === 'ready') {
        if (data.autoAssigned && data.assignedRider) {
          toast.success(`✅ Order ready! Rider: ${data.assignedRider.name} (${data.assignedRider.phone})`, { duration: 6000 });
        } else {
          toast.success('Order is ready! Finding a rider...', { duration: 4000 });
        }
      } else {
        const labels = { 'on-the-way': 'Rider picked up!', delivered: 'Delivered! 🎉' };
        toast.success(labels[status] || `Status updated: ${status}`);
      }
      fetchOrders(true);
    } catch {
      toast.error('Failed to update status. Please try again.');
    }
  };

  const cancelOrder = async (orderId) => {
    stopOrderAlert();
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      await API.patch(`/orders/${orderId}/cancel`, { reason: 'Cancelled by vendor', cancelledBy: 'vendor' });
      toast.success('Order cancelled.');
      fetchOrders(true);
    } catch {
      toast.error('Failed to cancel order.');
    }
  };

  // ── Group orders ──────────────────────────────
  const newOrders   = activeOrders.filter(o => ['placed', 'pending'].includes(o.status));
  const inProgress  = activeOrders.filter(o => ['accepted', 'preparing', 'packing'].includes(o.status));
  const readyOrders = activeOrders.filter(o => ['packed', 'ready'].includes(o.status));
  const onWay       = activeOrders.filter(o => ['picked_up', 'on-the-way'].includes(o.status));

  const urgentCount = activeOrders.filter(o => {
    const tat = TAT[o.status];
    if (!tat) return false;
    const start   = o.acceptedAt || o.preparingAt || o.packedAt || o.readyAt || o.createdAt;
    const elapsed = Math.floor((Date.now() - new Date(start)) / 1000);
    return (tat.total - elapsed) <= tat.urgentAt;
  }).length;

  return (
    <div style={S.page}>
      <style>{`
        @keyframes spin        { to { transform: rotate(360deg); } }
        @keyframes pulse       { 0%,100%{opacity:1} 50%{opacity:.4} }
        @keyframes urgentPulse { 0%,100%{opacity:1} 50%{opacity:.6} }
        @keyframes btnPulse    { 0%,100%{transform:scale(1)} 50%{transform:scale(1.03)} }
        @keyframes slideIn     { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={S.title}>Live Dashboard</div>
            {urgentCount > 0 && (
              <div style={{ background: '#ef4444', color: '#fff', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 800, animation: 'urgentPulse 1s infinite' }}>
                {urgentCount} urgent
              </div>
            )}
          </div>
          <div style={S.sub}>
            {loading
              ? <span style={{ color: '#aaa' }}>Loading...</span>
              : <><span style={S.dot} />{activeOrders.length} active orders</>
            }
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.iconBtn} onClick={() => { unlockAudio(); fetchOrders(); }}>
            <RefreshCw size={18} color='#555' style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
          <button
            style={{ ...S.soundBtn, background: soundOn ? '#ff6b35' : '#f0f0f0' }}
            onClick={() => { unlockAudio(); if (soundOn) stopOrderAlert(); setSoundOn(s => !s); }}
          >
            {soundOn ? <Bell size={16} color='#fff' /> : <BellOff size={16} color='#888' />}
            <span style={{ color: soundOn ? '#fff' : '#888', fontSize: 13, fontWeight: 600 }}>
              {soundOn ? 'Sound ON' : 'Sound OFF'}
            </span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={S.stats}>
        {[
          { label: 'New Orders',                               count: newOrders.length,  color: '#ef4444', bg: '#fef2f2' },
          { label: isGrocery ? 'Being Packed' : 'In Kitchen', count: inProgress.length, color: '#f97316', bg: '#fff7ed' },
          { label: 'On the Way',                              count: onWay.length,       color: '#10b981', bg: '#f0fdf4' },
        ].map(s => (
          <div key={s.label} style={{ ...S.stat, background: s.bg }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: s.color }}>{s.count}</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2, fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* TAT Guide */}
      <div style={S.tatGuide}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <Zap size={13} color='#f97316' fill='#f97316' />
          <span style={{ fontSize: 11, fontWeight: 800, color: '#f97316', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Target Delivery Times
          </span>
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {[
            { icon: <Zap size={12} color='#ef4444' />,    label: 'Accept', time: '50 sec',     color: '#ef4444' },
            { icon: isGrocery ? <Package size={12} color='#f97316' /> : <ChefHat size={12} color='#f97316' />,
              label: isGrocery ? 'Pack' : 'Cook',          time: '4 min',      color: '#f97316' },
            { icon: <Bike size={12} color='#8b5cf6' />,   label: 'Pickup', time: '1 min',      color: '#8b5cf6' },
            { icon: <Home size={12} color='#10b981' />,   label: 'Deliver',time: '10–15 min',  color: '#10b981' },
          ].map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#fff', borderRadius: 8, padding: '4px 10px', border: `1px solid ${t.color}22` }}>
              {t.icon}
              <span style={{ fontSize: 11, color: '#888' }}>{t.label}</span>
              <span style={{ fontSize: 11, fontWeight: 800, color: t.color }}>{t.time}</span>
            </div>
          ))}
        </div>
      </div>

      {/* New Orders */}
      {newOrders.length > 0 && (
        <div style={S.section}>
          <div style={{ ...S.secHead, borderColor: '#ef4444', background: '#fef2f2' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Zap size={15} color='#ef4444' fill='#ef4444' />
              <span style={{ fontWeight: 800, color: '#ef4444', fontSize: 14 }}>New Orders — Accept Now!</span>
            </div>
            <span style={{ ...S.badge, background: '#ef4444' }}>{newOrders.length}</span>
          </div>
          {newOrders.map(o => (
            <div key={o._id} style={{ animation: 'slideIn .3s ease both' }}>
              <OrderCard order={o} updateStatus={updateStatus} cancelOrder={cancelOrder} isGrocery={isGrocery} />
            </div>
          ))}
        </div>
      )}

      {/* In Kitchen / Packing */}
      {inProgress.length > 0 && (
        <div style={S.section}>
          <div style={{ ...S.secHead, borderColor: '#f97316', background: '#fff7ed' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Package size={15} color='#f97316' />
              <span style={{ fontWeight: 800, color: '#f97316', fontSize: 14 }}>
                {isGrocery ? 'Being Packed' : 'In the Kitchen'}
              </span>
            </div>
            <span style={{ ...S.badge, background: '#f97316' }}>{inProgress.length}</span>
          </div>
          {inProgress.map(o => (
            <div key={o._id} style={{ animation: 'slideIn .3s ease both' }}>
              <OrderCard order={o} updateStatus={updateStatus} cancelOrder={cancelOrder} isGrocery={isGrocery} />
            </div>
          ))}
        </div>
      )}

      {/* Ready — waiting for rider */}
      {readyOrders.length > 0 && (
        <div style={S.section}>
          <div style={{ ...S.secHead, borderColor: '#8b5cf6', background: '#f5f3ff', animation: 'urgentPulse 1.5s infinite' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Bike size={15} color='#8b5cf6' />
              <span style={{ fontWeight: 800, color: '#8b5cf6', fontSize: 14 }}>Ready — Waiting for Rider</span>
            </div>
            <span style={{ ...S.badge, background: '#8b5cf6' }}>{readyOrders.length}</span>
          </div>
          {readyOrders.map(o => (
            <div key={o._id} style={{ animation: 'slideIn .3s ease both' }}>
              {o.rider?.riderId ? (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '8px 12px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Bike size={14} color='#16a34a' />
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>
                    Rider assigned: {o.rider.name} · {o.rider.phone}
                  </span>
                </div>
              ) : (
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '8px 12px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Truck size={14} color='#f97316' />
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#f97316' }}>
                    Finding a rider... (auto-assign in progress)
                  </span>
                </div>
              )}
              <OrderCard order={o} updateStatus={updateStatus} cancelOrder={cancelOrder} isGrocery={isGrocery} />
            </div>
          ))}
        </div>
      )}

      {/* On the Way */}
      {onWay.length > 0 && (
        <div style={S.section}>
          <div style={{ ...S.secHead, borderColor: '#10b981', background: '#f0fdf4' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Bike size={15} color='#10b981' />
              <span style={{ fontWeight: 800, color: '#10b981', fontSize: 14 }}>Out for Delivery</span>
            </div>
            <span style={{ ...S.badge, background: '#10b981' }}>{onWay.length}</span>
          </div>
          {onWay.map(o => (
            <div key={o._id} style={{ animation: 'slideIn .3s ease both' }}>
              <OrderCard order={o} updateStatus={updateStatus} cancelOrder={cancelOrder} isGrocery={isGrocery} />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && activeOrders.length === 0 && (
        <div style={S.empty}>
          <ShoppingBag size={64} color='#e5e7eb' />
          <div style={{ fontWeight: 800, color: '#9ca3af', marginTop: 16, fontSize: 17 }}>No active orders</div>
          <div style={{ fontSize: 13, color: '#d1d5db', marginTop: 6 }}>New orders will appear here automatically.</div>
        </div>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// STYLES
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const S = {
  page:     { padding: '16px', paddingBottom: 88, background: '#f3f4f6', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif" },
  header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 },
  title:    { fontSize: 22, fontWeight: 900, color: '#111827', letterSpacing: '-0.5px' },
  sub:      { fontSize: 13, color: '#10b981', marginTop: 3, display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600 },
  dot:      { width: 8, height: 8, borderRadius: '50%', background: '#10b981', display: 'inline-block', animation: 'pulse 1.5s infinite' },
  iconBtn:  { padding: 8, borderRadius: 12, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb', cursor: 'pointer' },
  soundBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none', cursor: 'pointer' },
  stats:    { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 12 },
  stat:     { borderRadius: 14, padding: '13px 10px', textAlign: 'center', boxShadow: '0 1px 6px rgba(0,0,0,0.05)' },
  tatGuide: { background: '#fff', borderRadius: 14, padding: '12px 14px', marginBottom: 14, border: '1px solid #fed7aa', boxShadow: '0 1px 6px rgba(0,0,0,0.04)' },
  section:  { marginBottom: 14 },
  secHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 12, border: '2px solid', marginBottom: 10 },
  badge:    { color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 800 },
  empty:    { textAlign: 'center', padding: '80px 20px' },
};

const C = {
  card:        { background: '#fff', borderRadius: 16, padding: '14px', marginBottom: 10, transition: 'border .3s, box-shadow .3s' },
  head:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  id:          { fontWeight: 900, fontSize: 16, color: '#111827', letterSpacing: '-0.3px' },
  statusBadge: { padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 },
  age:         { display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#9ca3af' },
  row:         { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  callBtn:     { display: 'flex', alignItems: 'center', gap: 4, background: '#f0fdf4', color: '#16a34a', padding: '6px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, textDecoration: 'none', border: '1px solid #bbf7d0' },
  items:       { background: '#f9fafb', borderRadius: 12, padding: '10px 12px', marginBottom: 10, border: '1px solid #f3f4f6' },
  item:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px dashed #f3f4f6' },
  addr:        { display: 'flex', alignItems: 'center', gap: 6, background: '#f9fafb', padding: '7px 10px', borderRadius: 8, marginBottom: 10, border: '1px solid #f3f4f6' },
  foot:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  payMode:     { fontSize: 11, color: '#9ca3af', background: '#f3f4f6', padding: '2px 8px', borderRadius: 6, fontWeight: 600 },
  actions:     { display: 'flex', gap: 8 },
  acceptBtn:   { flex: 2, padding: '12px', background: '#10b981', color: '#fff', borderRadius: 12, fontWeight: 800, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: '0 4px 12px rgba(16,185,129,0.3)', border: 'none', cursor: 'pointer' },
  rejectBtn:   { flex: 1, padding: '12px', background: '#fee2e2', color: '#ef4444', borderRadius: 12, fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, border: '1px solid #fecaca', cursor: 'pointer' },
  nextBtn:     { flex: 1, padding: '12px', color: '#fff', borderRadius: 12, fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, border: 'none', cursor: 'pointer', width: '100%' },
};